// pages/sections/ClientContactCreateRecord.js
// 2c — lets a reviewer author a brand-new contact record for a client instead
// of picking a winning source per field. They can pre-populate from an existing
// data source (PE / Knuula / CCH); when only ONE source has data, the form is
// auto-filled from it. Saving stores the record (parent → create-record action);
// clearing reverts to the vendor comparison.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export function isValidEmail(v) {
  return typeof v === 'string' && EMAIL_RE.test(v.trim());
}

// Base editable fields (spouse email is appended when the client is MFJ).
const BASE_FIELDS = [
  { key: 'address',  label: 'Address' },
  { key: 'city',     label: 'City' },
  { key: 'state',    label: 'State' },
  { key: 'postCode', label: 'Postal Code' },
  { key: 'email',    label: 'Email' },
  { key: 'phone',    label: 'Phone' },
];

// Map a vendor's record to the editable record shape.
function sourceRecord(row, src) {
  const v = row.vendors || {};
  if (src === 'pe') {
    const pe = v.pe || {};
    return { address: pe.address, city: pe.city, state: pe.state, postCode: pe.postCode, email: pe.email, phone: pe.phone, spouseEmail: row.spouseEmail || '' };
  }
  if (src === 'knuula') {
    const k = v.knuula || {};
    return { address: k.address, city: k.city, state: k.state, postCode: k.zip, email: k.email, phone: '' };
  }
  if (src === 'cch') {
    const c = v.cch || {};
    return { address: c.address, city: c.city, state: c.state, postCode: c.postCode, email: c.email, phone: c.phone };
  }
  return {};
}

const SOURCE_LABELS = { pe: 'PE (master)', knuula: 'Knuula', cch: 'CCH' };

function blankRecord(fields) {
  const r = {};
  for (const f of fields) r[f.key] = '';
  return r;
}

function normalize(rec, fields) {
  const out = {};
  for (const f of fields) out[f.key] = rec[f.key] == null ? '' : String(rec[f.key]);
  return out;
}

// The fields a reviewer MUST supply to use this record — exactly the ones
// currently flagged on the row (an unresolved vendor mismatch, or an
// invalid/missing email / spouse email) that are editable here. Fields that
// aren't flagged stay optional, so we never trap the reviewer over a value the
// client legitimately lacks (e.g. no phone). Paper EL suppresses email-related
// requirements. `manager` mismatches aren't editable here, so they're excluded.
function requiredKeysFor(row, formKeys) {
  const paper = row.isPaper === true;
  const fld = row.fields || {};
  const req = new Set();
  for (const k of formKeys) {
    if (k === 'spouseEmail') continue;          // handled via the spouse signals below
    if (paper && k === 'email') continue;       // paper EL: email irrelevant
    const f = fld[k];
    if (f && f.mismatch && !f.resolvedSource) req.add(k);   // unresolved vendor mismatch
  }
  if (!paper && row.emailValid === false) req.add('email');
  if (!paper && row.isMFJ && (row.spouseEmailValid === false || row.spouseEmailMismatch === true)) {
    req.add('spouseEmail');
  }
  return req;
}

export default function ClientContactCreateRecord({ row, readOnly, onCreate, onClear }) {
  const fields = useMemo(
    () => (row.isMFJ ? [...BASE_FIELDS, { key: 'spouseEmail', label: 'Spouse Email' }] : BASE_FIELDS),
    [row.isMFJ]
  );

  // Sources that actually have data (PE is always present).
  const availableSources = useMemo(() => {
    const v = row.vendors || {};
    return ['pe', 'knuula', 'cch'].filter(s => (s === 'pe' ? !!v.pe : !!v[s]));
  }, [row.vendors]);

  const existing = row.createdRecord || null;
  const [editing, setEditing] = useState(false);
  const [rec, setRec] = useState(() => blankRecord(fields));
  const [copyFrom, setCopyFrom] = useState('');
  // The latest saved record, so a refused save (the page puts the old record back) can be noticed.
  const existingRef = useRef(existing);
  existingRef.current = existing;

  // When the editor opens fresh (no existing record), seed it: from the single
  // source if there's exactly one, otherwise leave blank for the reviewer.
  function beginCreate() {
    if (existing) {
      setRec(normalize(existing, fields));
      setCopyFrom('');
    } else if (availableSources.length === 1) {
      setRec(normalize(sourceRecord(row, availableSources[0]), fields));
      setCopyFrom(availableSources[0]);
    } else {
      setRec(blankRecord(fields));
      setCopyFrom('');
    }
    setEditing(true);
  }

  // Keep the field set in sync if MFJ flips while the editor is open.
  useEffect(() => {
    if (editing) setRec(prev => normalize(prev, fields));
  }, [fields, editing]);

  function applyCopy(src) {
    setCopyFrom(src);
    if (src) setRec(normalize(sourceRecord(row, src), fields));
  }

  function setField(key, value) {
    setRec(prev => ({ ...prev, [key]: value }));
  }

  // Fields the reviewer must fill to clear this row (the currently-flagged ones).
  const requiredKeys = useMemo(() => requiredKeysFor(row, fields.map(f => f.key)), [row, fields]);

  // Per-field validity for highlighting + the save gate:
  //   'required' — a flagged field left blank
  //   'invalid'  — an email/spouse email with a non-empty but malformed value
  function fieldError(key) {
    const val = String(rec[key] || '').trim();
    if (requiredKeys.has(key) && !val) return 'required';
    if ((key === 'email' || key === 'spouseEmail') && val && !isValidEmail(val)) return 'invalid';
    return null;
  }

  // Optimistic: the form closes and the card shows the new record at once (the page patches the row and
  // POSTs in the background). If the server refuses, the page puts the old record back and toasts the
  // error; we then reopen the form with what was typed so nothing has to be re-entered.
  function save() {
    // Block until every FLAGGED field has a value, and any entered email is
    // valid. Unflagged fields stay optional. Toast the specific problems.
    const problems = [];
    for (const f of fields) {
      const err = fieldError(f.key);
      if (err === 'required')     problems.push(`${f.label} is required`);
      else if (err === 'invalid') problems.push(`${f.label} must be a valid email`);
    }
    if (problems.length) {
      toast.error(`Fix before saving — ${problems.join('; ')}.`);
      return;
    }
    const submitted = normalize(rec, fields);
    const reopen = () => { setRec(submitted); setEditing(true); };
    const stuck = () => !fields.every(f => String(existingRef.current?.[f.key] || '').trim() === String(submitted[f.key] || '').trim());
    setEditing(false);
    Promise.resolve(onCreate(submitted)).then(() => { if (stuck()) reopen(); }, reopen);
  }

  // Optimistic: the record disappears at once; a refusal puts it back (the page toasts the error).
  function clear() {
    Promise.resolve(onClear()).catch(() => {});
    setEditing(false);
  }

  // Rendered as the 5th card in the drilldown grid. Shows the created record as
  // field rows (peer to the vendor cards); the edit form expands the card to
  // full width so the inputs have room.
  return (
    <div className="cc-card cc-card--create">
      <div className="cc-card-header">
        <span className="cc-card-vendor">
          <i className="fas fa-user-pen" aria-hidden="true" /> New Record
        </span>
        {existing && <span className="cc-card-empty-tag cc-card-tag--active">in use</span>}
      </div>
      <div className="cc-card-body">
        {editing ? (
          <div className="cc-create-form">
            {availableSources.length > 0 && (
              <label className="cc-create-copy">
                Copy from{' '}
                <select value={copyFrom} onChange={e => applyCopy(e.target.value)}>
                  <option value="">—</option>
                  {availableSources.map(s => <option key={s} value={s}>{SOURCE_LABELS[s]}</option>)}
                </select>
              </label>
            )}
            {requiredKeys.size > 0 && (
              <p className="cc-create-required-note">
                Fields marked <span className="cc-req-star">*</span> are flagged on this client and must be filled before you can use this record.
              </p>
            )}
            {/* Same vertical field-row layout as the saved card, but editable. */}
            {fields.map(f => {
              const err = fieldError(f.key);
              const required = requiredKeys.has(f.key);
              return (
                <div className="cc-field-row" key={f.key}>
                  <div className="cc-field-label">
                    {f.label}{f.key === 'spouseEmail' ? ' (MFJ)' : ''}
                    {required && <span className="cc-req-star" title="Required — currently flagged">*</span>}
                  </div>
                  <div className="cc-field-value">
                    <input
                      className={`cc-field-input ${err ? 'cc-input-invalid' : ''}`}
                      value={rec[f.key] || ''}
                      onChange={e => setField(f.key, e.target.value)}
                    />
                  </div>
                </div>
              );
            })}
            <div className="cc-create-actions">
              <button type="button" className="cc-create-btn cc-create-btn--ghost" onClick={() => setEditing(false)}>Cancel</button>
              <button type="button" className="cc-create-btn" onClick={save}>
                {existing ? 'Save changes' : 'Save record'}
              </button>
            </div>
          </div>
        ) : existing ? (
          <>
            {fields.map(f => (
              <div className="cc-field-row" key={f.key}>
                <div className="cc-field-label">{f.label}</div>
                <div className="cc-field-value">
                  {existing[f.key] ? existing[f.key] : <span className="cc-cell-empty">—</span>}
                </div>
              </div>
            ))}
            {!readOnly && (
              <div className="cc-created-actions">
                <button type="button" className="cc-create-btn" onClick={beginCreate}>Edit</button>
                <button type="button" className="cc-create-btn cc-create-btn--ghost" onClick={clear}>Clear</button>
              </div>
            )}
          </>
        ) : readOnly ? (
          <div className="cc-card-empty">No reviewer-entered record.</div>
        ) : (
          <div className="cc-create-collapsed">
            <p className="cc-create-muted">
              To correct any field, enter the right values here — pick a source to pre-fill, then edit.
            </p>
            <button type="button" className="cc-create-btn" onClick={beginCreate}>
              <i className="fas fa-plus" aria-hidden="true" /> Create new record
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
